import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate, useParams, useLocation} from "react-router-dom";
import {Box, Button, HStack, IconButton, Skeleton, Table, Text, VStack,} from "@chakra-ui/react";
import {HiArrowRight, HiOutlineCpuChip, HiStar} from "react-icons/hi2";
import useSetPageHeader from "@/components/hooks/useSetPageHeader";
import {ModelService} from "@/services/ModelService";
import {fromApiResponse} from "@/services/jobOfferMapper";
import JobOfferFormModal from "@/components/jobOffer/JobOfferFormModal";
import {toaster} from "@/components/ui/toaster";
import {RecruitmentStatusBadge} from "@/pages/models/RecruitmentStatusBadge";
import CampaignsSection from "@/pages/models/CampaignsSection";
import useOfferPreparationPolling from "@/components/hooks/useOfferPreparationPolling";
import {isOfferPreparing, isOfferLinkLocked, offerLinkLockedKey} from "@/utils/offerPreparation";

const isDraftOrInactive = (status) => ["draft", "inactive"].includes(status?.toLowerCase());

const ScenarioDetailPage = () => {
    const { t, i18n } = useTranslation("models");
    const formatDate = (value) => new Date(value).toLocaleDateString(i18n.language);
    const { id, scenarioId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    // NewRecruitmentModal pre-fetches this same data before navigating here (see its
    // goToScenario) so the create form can appear immediately instead of behind this
    // page's own loading spinner -- see the fetch effect below for the silent-refresh half.
    const prefetchedAgent = location.state?.prefetchedAgent ?? null;
    const [agent, setAgent] = useState(prefetchedAgent);
    const [scenario, setScenario] = useState(() => prefetchedAgent?.scenarios?.find(s => s.id === scenarioId) ?? null);
    const [recruitments, setRecruitments] = useState(() => location.state?.prefetchedRecruitments ?? []);
    useOfferPreparationPolling(recruitments, (rid, patch) =>
        setRecruitments((prev) => prev.map((r) => (r.id === rid ? { ...r, ...patch } : r))));
    const [isLoading, setIsLoading] = useState(!prefetchedAgent);
    const [jobOfferModal, setJobOfferModal] = useState(null);
    const [favLoadingId, setFavLoadingId] = useState(null);
    const [editLoadingId, setEditLoadingId] = useState(null);

    useSetPageHeader(t("scenarioDetailPage.title"), "", <HiOutlineCpuChip />);

    // Lets the "Nowa rekrutacja" picker on the recruitment dashboard land straight on this
    // scenario's create form when it's the only agent/scenario the user has, instead of
    // requiring an extra click here. Clears the flag from history so back/forward can't replay it.
    useEffect(() => {
        if (location.state?.autoOpenCreate) {
            setJobOfferModal({ mode: "create" });
            navigate(location.pathname, { replace: true, state: null });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchRecruitments = () =>
        ModelService.getRecruitments(scenarioId).then((data) => setRecruitments(data?.items ?? []));

    useEffect(() => {
        // Already hydrated from the picker's prefetch -- refresh quietly in the background
        // instead of flashing the spinner back on for data the user is already looking at.
        const alreadyHydrated = agent && scenario?.id === scenarioId;
        if (!alreadyHydrated) setIsLoading(true);
        ModelService.getById(id).then((agentData) => {
            setAgent(agentData);
            const scenarioData = agentData?.scenarios?.find(s => s.id === scenarioId) ?? null;
            setScenario(scenarioData);

            if (agentData?.type === "SalesManager") {
                setIsLoading(false);
                return;
            }

            ModelService.getRecruitments(scenarioId).then((recruitmentsData) => {
                setRecruitments(recruitmentsData?.items ?? []);
                setIsLoading(false);
            });
        });
    }, [scenarioId]);

    const handleEndRecruitment = (recruitmentId) => {
        ModelService.endRecruitment(scenarioId, recruitmentId).then(() => {
            setRecruitments((prev) =>
                prev.map((r) =>
                    r.id === recruitmentId ? { ...r, status: "ended" } : r
                )
            );
        });
    };

    const handleToggleFavorite = async (id) => {
        setFavLoadingId(id);
        const updated = await ModelService.toggleFavorite(id);
        setRecruitments((prev) =>
            prev.map((r) => r.id === id ? { ...r, isFavorite: updated.isFavorite } : r)
        );
        setFavLoadingId(null);
    };

    const handleEditRecruitment = async (recruitmentId) => {
        setEditLoadingId(recruitmentId);
        try {
            const data = await ModelService.getRecruitmentById(recruitmentId);
            setJobOfferModal({ mode: "edit", recruitmentId, initialValues: fromApiResponse(data) });
        } finally {
            setEditLoadingId(null);
        }
    };

    const handleCopyLink = (chatPath) => {
        const link = `${window.location.origin}/c${chatPath}`;
        navigator.clipboard.writeText(link);
        toaster.create({ type: "success", title: t("scenarioDetailPage.linkCopied") });
    };

    if (isLoading) {
        return (
            <VStack w="full" py={6} gap={6} align="flex-start">
                <Box w="full" bg="white" border="1px solid" borderColor="gray.200" borderRadius={12} p={5}>
                    <HStack justify="space-between" align="flex-start" gap={4} flexWrap="wrap">
                        <VStack align="flex-start" gap={2}>
                            <Skeleton height="22px" width="200px" borderRadius={6} />
                            <Skeleton height="14px" width="240px" borderRadius={4} />
                        </VStack>
                        <Skeleton height="38px" width="140px" borderRadius={8} />
                    </HStack>
                </Box>
                <VStack w="full" align="flex-start" gap={3}>
                    <Skeleton height="18px" width="140px" borderRadius={4} />
                    <VStack w="full" gap={2} align="stretch">
                        {Array.from({ length: 4 }).map((_, i) => (
                            <Box key={i} w="full" bg="white" border="1px solid" borderColor="gray.200" borderRadius={10} p={4}>
                                <HStack justify="space-between">
                                    <Skeleton height="14px" width="35%" borderRadius={4} />
                                    <Skeleton height="20px" width="80px" borderRadius="999px" />
                                </HStack>
                            </Box>
                        ))}
                    </VStack>
                </VStack>
            </VStack>
        );
    }

    if (!scenario) {
        return (
            <VStack w="full" py={12} align="center">
                <Text color="gray.500">{t("scenarioDetailPage.notFound")}</Text>
            </VStack>
        );
    }

    return (
        <VStack w="full" py={6} gap={6} align="flex-start">
            <Box
                w="full"
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                borderRadius={12}
                p={5}
            >
                <HStack justify="space-between" align="flex-start" gap={4} flexWrap="wrap">
                    <VStack align="flex-start" gap={2}>
                        <Text fontWeight={700} fontSize="xl" color="gray.900">
                            {scenario.name}
                        </Text>
                        <HStack gap={4} flexWrap="wrap">
                            <Text color="gray.500" fontSize="sm">
                                {t("scenarioDetailPage.created")}: {formatDate(scenario.createdAtUtc)}
                            </Text>
                            <Text color="gray.500" fontSize="sm">
                                {t("scenarioDetailPage.edited")}: {formatDate(scenario.editedAtUtc)}
                            </Text>
                        </HStack>
                    </VStack>
                    {agent?.type !== "SalesManager" && (
                        <Button variant="primary" flexShrink={0} onClick={() => setJobOfferModal({ mode: "create" })}>
                            {t("scenarioDetailPage.newRecruitment")}
                        </Button>
                    )}
                </HStack>
            </Box>

            {agent?.type === "SalesManager" ? (
                <CampaignsSection agentId={id} agentName={agent?.name} scenarioId={scenarioId} />
            ) : (
            <VStack w="full" align="flex-start" gap={3}>
                <Text fontWeight={700} fontSize="lg" color="gray.900">
                    {t("scenarioDetailPage.listTitle")}
                </Text>

                {/* Desktop table */}
                <Box
                    display={{ base: 'none', md: 'block' }}
                    w="full"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius={12}
                    overflowX="auto"
                >
                    <Table.Root minW="720px">
                        <Table.Header>
                            <Table.Row bg="gray.50" borderBottom="1px solid" borderColor="gray.200">
                                <Table.ColumnHeader
                                    color="gray.500" fontWeight={600} fontSize="xs"
                                    textTransform="uppercase" letterSpacing="wider"
                                    py={3} px={5} border="none"
                                >
                                    {t("scenarioDetailPage.colPosition")}
                                </Table.ColumnHeader>
                                <Table.ColumnHeader
                                    color="gray.500" fontWeight={600} fontSize="xs"
                                    textTransform="uppercase" letterSpacing="wider"
                                    py={3} px={5} border="none"
                                >
                                    {t("scenarioDetailPage.colStatus")}
                                </Table.ColumnHeader>
                                <Table.ColumnHeader
                                    color="gray.500" fontWeight={600} fontSize="xs"
                                    textTransform="uppercase" letterSpacing="wider"
                                    py={3} px={5} border="none"
                                >
                                    {t("scenarioDetailPage.colWhatsapp")}
                                </Table.ColumnHeader>
                                <Table.ColumnHeader
                                    color="gray.500" fontWeight={600} fontSize="xs"
                                    textTransform="uppercase" letterSpacing="wider"
                                    py={3} px={5} border="none"
                                >
                                    {t("scenarioDetailPage.colCreatedAt")}
                                </Table.ColumnHeader>
                                <Table.ColumnHeader
                                    color="gray.500" fontWeight={600} fontSize="xs"
                                    textTransform="uppercase" letterSpacing="wider"
                                    py={3} px={5} border="none" textAlign="right"
                                >
                                    {t("scenarioDetailPage.colActions")}
                                </Table.ColumnHeader>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {recruitments.map((recruitment) => (
                                <Table.Row
                                    key={recruitment.id}
                                    bg="white"
                                    borderBottom="1px solid"
                                    borderColor="gray.200"
                                    _hover={{ bg: "gray.50" }}
                                    transition="background 0.15s"
                                    onClick={() => navigate(`/models/${id}/recruitments/${recruitment.id}/candidates`)}
                                    cursor={'pointer'}
                                >
                                    <Table.Cell py={4} px={5} border="none">
                                        <Text color="gray.900" fontSize="sm" fontWeight={500}>
                                            {recruitment.jobTitle}
                                        </Text>
                                    </Table.Cell>
                                    <Table.Cell py={4} px={5} border="none">
                                        <RecruitmentStatusBadge status={recruitment.status} preparationStatus={recruitment.preparationStatus} preparationProgress={recruitment.preparationProgress} />
                                    </Table.Cell>
                                    <Table.Cell py={4} px={5} border="none">
                                        <Text color="gray.500" fontSize="sm">-</Text>
                                    </Table.Cell>
                                    <Table.Cell py={4} px={5} border="none">
                                        <Text color="gray.500" fontSize="sm">
                                            {formatDate(recruitment.createdAtUtc)}
                                        </Text>
                                    </Table.Cell>
                                    <Table.Cell py={4} px={5} border="none" textAlign="right">
                                        <HStack gap={2} justify="flex-end">
                                            <IconButton
                                                variant="ghost"
                                                size="sm"
                                                aria-label={t("scenarioDetailPage.favorite")}
                                                color={recruitment.isFavorite ? "yellow.400" : "gray.500"}
                                                loading={favLoadingId === recruitment.id}
                                                onClick={(e) => { e.stopPropagation(); handleToggleFavorite(recruitment.id); }}
                                            >
                                                <HiStar />
                                            </IconButton>
                                            {!isDraftOrInactive(recruitment.status) &&
                                                <Button
                                                    variant="outlined"
                                                    size="sm"
                                                    color="red.500"
                                                    borderColor="red.200"
                                                    _hover={{ bg: "red.50" }}
                                                    onClick={(e) => { e.stopPropagation(); handleEndRecruitment(recruitment.id); }}
                                                    disabled={recruitment.status === "ended"}
                                                >
                                                    {t("scenarioDetailPage.end")}
                                                </Button>
                                            }
                                            {!isDraftOrInactive(recruitment.status) &&
                                                <Button
                                                    variant="outlined"
                                                    size="sm"
                                                    disabled={isOfferLinkLocked(recruitment)}
                                                    title={isOfferLinkLocked(recruitment) ? t(offerLinkLockedKey(recruitment)) : undefined}
                                                    onClick={(e) => { e.stopPropagation(); handleCopyLink(recruitment.chatPath); }}
                                                >
                                                    {t("scenarioDetailPage.copyLink")}
                                                </Button>
                                            }
                                            <Button
                                                variant="outlined"
                                                size="sm"
                                                loading={editLoadingId === recruitment.id}
                                                disabled={isOfferPreparing(recruitment)}
                                                onClick={(e) => { e.stopPropagation(); handleEditRecruitment(recruitment.id); }}
                                            >
                                                {t("scenarioDetailPage.edit")}
                                            </Button>
                                            <IconButton
                                                variant="outlined"
                                                size="sm"
                                                aria-label={t("scenarioDetailPage.details")}
                                                onClick={() => navigate(`/models/${id}/recruitments/${recruitment.id}/candidates`)}
                                            >
                                                <HiArrowRight />
                                            </IconButton>
                                        </HStack>
                                    </Table.Cell>
                                </Table.Row>
                            ))}
                        </Table.Body>
                    </Table.Root>
                </Box>

                {/* Mobile card list */}
                <VStack
                    display={{ base: 'flex', md: 'none' }}
                    w="full"
                    gap={2}
                >
                    {recruitments.map((recruitment) => (
                        <Box
                            key={recruitment.id}
                            w="full"
                            bg="white"
                            border="1px solid"
                            borderColor="gray.200"
                            borderRadius={10}
                            p={4}
                            cursor="pointer"
                            onClick={() => navigate(`/models/${id}/recruitments/${recruitment.id}/candidates`)}
                            _active={{ opacity: 0.8 }}
                        >
                            <HStack justify="space-between" align="flex-start" mb={2}>
                                <VStack align="flex-start" gap={0} flex={1} minW={0}>
                                    <HStack gap={2} flexWrap="wrap" justifyContent="space-between" w={'full'}>
                                        <Text color="gray.900" fontSize="sm" fontWeight={600}>
                                            {recruitment.jobTitle}
                                        </Text>
                                        <Box>
                                            <RecruitmentStatusBadge status={recruitment.status} preparationStatus={recruitment.preparationStatus} preparationProgress={recruitment.preparationProgress} />
                                            <IconButton
                                                variant="ghost"
                                                size="sm"
                                                aria-label={t("scenarioDetailPage.favorite")}
                                                color={recruitment.isFavorite ? "yellow.400" : "gray.500"}
                                                loading={favLoadingId === recruitment.id}
                                                flexShrink={0}
                                                onClick={(e) => { e.stopPropagation(); handleToggleFavorite(recruitment.id); }}
                                            >
                                                <HiStar />
                                            </IconButton>
                                        </Box>
                                    </HStack>
                                    <Text color="gray.500" fontSize="xs">
                                        {t("scenarioDetailPage.date")}: {formatDate(recruitment.createdAtUtc)}
                                    </Text>
                                </VStack>
                            </HStack>
                            <HStack gap={2} justify="flex-end" flexWrap="wrap">
                                {!isDraftOrInactive(recruitment.status) &&
                                    <Button
                                        variant="outlined"
                                        size="sm"
                                        color="red.500"
                                        borderColor="red.200"
                                        _hover={{ bg: "red.50" }}
                                        onClick={(e) => { e.stopPropagation(); handleEndRecruitment(recruitment.id); }}
                                        disabled={recruitment.status === "ended"}
                                    >
                                        {t("scenarioDetailPage.end")}
                                    </Button>
                                }
                                <Button
                                    variant="outlined"
                                    size="sm"
                                    loading={editLoadingId === recruitment.id}
                                    onClick={(e) => { e.stopPropagation(); handleEditRecruitment(recruitment.id); }}
                                >
                                    {t("scenarioDetailPage.edit")}
                                </Button>
                                <IconButton
                                    variant="outlined"
                                    size="sm"
                                    aria-label={t("scenarioDetailPage.details")}
                                    onClick={(e) => { e.stopPropagation(); navigate(`/models/${id}/recruitments/${recruitment.id}/candidates`); }}
                                >
                                    <HiArrowRight />
                                </IconButton>
                            </HStack>
                        </Box>
                    ))}
                </VStack>
            </VStack>
            )}

            {agent?.type !== "SalesManager" && jobOfferModal && (
                <JobOfferFormModal
                    onClose={() => setJobOfferModal(null)}
                    initialValues={jobOfferModal.initialValues}
                    onSubmit={(values, isDraft) => {
                        if (isDraft) {
                            return ModelService.saveDraft({ ...values, scenarioId: values.scenarioId ?? scenarioId }).then(fetchRecruitments);
                        }
                        if (jobOfferModal.mode === "create") {
                            return ModelService.createRecruitment(scenarioId, values).then(() => {
                                fetchRecruitments();
                                setJobOfferModal(null);
                            });
                        } else {
                            return ModelService.updateRecruitment(jobOfferModal.recruitmentId, values).then(() => {
                                fetchRecruitments();
                                setJobOfferModal(null);
                            });
                        }
                    }}
                />
            )}
        </VStack>
    );
};

export default ScenarioDetailPage;
